import { bossDeathAge } from '../defeat';
/**
 * Plan 07 — skeletal prototype: the CLIP LIBRARY + pure sampler + per-actor pose evaluation.
 *
 * Everything in this module is PURE (no Babylon, no DOM, no RNG, no clock): a pose is a function of
 * (SimState snapshot, static level data, slot identity) only — the same P5.3 discipline the
 * procedural crowd obeys, generalized from whole-mesh matrices to per-bone transforms. Pause ⇒
 * freeze; two updates on one snapshot ⇒ byte-identical poses (pinned in clips.test.ts).
 *
 * Clip metadata follows the rigged-game-asset contract: every clip carries a stable id, a semantic
 * role, an explicit loop policy, a root-motion policy (all clips are IN-PLACE — formation placement
 * and root-node transforms are owned by the actor classes, never by a clip), a measured duration,
 * named contact events, and provenance.
 *
 * Blend model (kept deliberately simple and total-order deterministic):
 *   * BASE clips (walk, idle) are authored bind-relative and sampled at their own phase.
 *   * ADDITIVE clips (fire recoil, slam, flinch) are bind-relative DELTAS, scaled by a pure weight
 *     and summed on top of the base.
 *   * DEATH clips are absolute poses that REPLACE the blend via a short crossfade, then hold their
 *     last key (corpse) — non-looping, restartable by construction (pure per snapshot).
 *
 * Euler convention: [pitch=x, yaw=y, roll=z] radians, bind-relative, composed by Babylon's
 * FromEulerAngles (YXZ) at apply time. Small authored angles keep composition-order drift negligible.
 */

// ---------------------------------------------------------------------------
// Clip types (authoring form — bone names, readable)
// ---------------------------------------------------------------------------

export type ClipRole =
  | 'idle'
  | 'walk'
  | 'run'
  | 'locomotion'
  | 'primary-attack'
  | 'hit'
  | 'death';

export interface ContactEvent {
  /** Normalized time in [0, 1]. */
  u: number;
  name: string;
}

export interface ClipKey {
  u: number;
  /** Bind-relative euler [rx, ry, rz] radians at key u. */
  rot: readonly [number, number, number];
  /** Optional bind-relative position offset [dx, dy, dz] metres at key u (usually the root only). */
  pos?: readonly [number, number, number];
}

export interface BoneTrack {
  bone: string;
  keys: readonly ClipKey[];
}

export interface Clip {
  id: string;
  role: ClipRole;
  loop: boolean;
  rootMotion: 'in-place';
  durationSeconds: number;
  contactEvents: readonly ContactEvent[];
  tracks: readonly BoneTrack[];
  provenance: string;
}

// ---------------------------------------------------------------------------
// Soldier clips (character profile; prototype lanes: walk / primary-attack / death)
// ---------------------------------------------------------------------------

/** March cycle period (s). 1.9 Hz leg swing family — the pinned bob rate's phase (P5.3). */
export const SOLDIER_WALK_DURATION = 0.62;
export const SOLDIER_FIRE_DURATION = 0.35;
export const SOLDIER_DEATH_DURATION = 1.15;
/** How long a fallen soldier is kept on the bridge (s) before the corpse stops rendering. */
export const SOLDIER_CORPSE_HOLD = 3.5;
/** The corpse sinks away over the last part of the hold (plan 08 G5). */
export const SOLDIER_CORPSE_FADE = 0.5;
/** Death crossfade over the first fraction of the clip (walk → fall). */
const DEATH_XFADE = 0.12;

/** Helper: a swing track — one sine-ish cycle over 5 keys (authoring shorthand, still literal). */
function swing(bone: string, peak: number, phase: 0 | 1): BoneTrack {
  const s = phase === 0 ? 1 : -1;
  return {
    bone,
    keys: [
      { u: 0, rot: [s * peak, 0, 0] },
      { u: 0.25, rot: [0, 0, 0] },
      { u: 0.5, rot: [-s * peak, 0, 0] },
      { u: 0.75, rot: [0, 0, 0] },
      { u: 1, rot: [s * peak, 0, 0] },
    ],
  };
}

/** Helper: an additive recoil-shaped track: instant onset, exponential-ish decay to bind. */
function recoil(bone: string, rx: number): BoneTrack {
  return {
    bone,
    keys: [
      { u: 0, rot: [rx, 0, 0] },
      { u: 0.25, rot: [rx * 0.6, 0, 0] },
      { u: 0.5, rot: [rx * 0.3, 0, 0] },
      { u: 0.75, rot: [rx * 0.1, 0, 0] },
      { u: 1, rot: [0, 0, 0] },
    ],
  };
}

export const SOLDIER_CLIP_WALK: Clip = {
  id: 'soldier-walk',
  role: 'walk',
  loop: true,
  rootMotion: 'in-place',
  durationSeconds: SOLDIER_WALK_DURATION,
  contactEvents: [
    { u: 0, name: 'left-contact' },
    { u: 0.5, name: 'right-contact' },
  ],
  tracks: [
    // Amplitudes tuned for legibility at the gameplay camera (soldiers ≈ 40 px tall): a march read
    // needs a visible stride, verified pixel-side via the glm vision lane (plan 07).
    swing('thighL', 0.34, 0),
    swing('thighR', 0.34, 1),
    // Knees bend as the thigh swings back (foot clearance), peak lagging the thigh by a quarter.
    {
      bone: 'shinL',
      keys: [
        { u: 0, rot: [0.06, 0, 0] },
        { u: 0.25, rot: [0.2, 0, 0] },
        { u: 0.5, rot: [0.45, 0, 0] },
        { u: 0.75, rot: [0.2, 0, 0] },
        { u: 1, rot: [0.06, 0, 0] },
      ],
    },
    {
      bone: 'shinR',
      keys: [
        { u: 0, rot: [0.45, 0, 0] },
        { u: 0.25, rot: [0.2, 0, 0] },
        { u: 0.5, rot: [0.06, 0, 0] },
        { u: 0.75, rot: [0.2, 0, 0] },
        { u: 1, rot: [0.45, 0, 0] },
      ],
    },
    swing('footL', 0.2, 1),
    swing('footR', 0.2, 0),
    // Rifle carry: tiny counter-swing on the shoulders, spine counter-rotation.
    swing('upperArmL', 0.05, 0),
    swing('upperArmR', 0.05, 1),
    {
      bone: 'hips',
      keys: [
        { u: 0, rot: [0, 0.03, 0] },
        { u: 0.25, rot: [0, 0, 0] },
        { u: 0.5, rot: [0, -0.03, 0] },
        { u: 0.75, rot: [0, 0, 0] },
        { u: 1, rot: [0, 0.03, 0] },
      ],
    },
    {
      bone: 'spine',
      keys: [
        { u: 0, rot: [0.02, 0, 0] },
        { u: 0.25, rot: [0, 0, 0] },
        { u: 0.5, rot: [-0.02, 0, 0] },
        { u: 0.75, rot: [0, 0, 0] },
        { u: 1, rot: [0.02, 0, 0] },
      ],
    },
  ],
  provenance: 'authored keyframes (plan 07), phase-locked to the P5.3 bob family (1.9 Hz)',
};

export const SOLDIER_CLIP_FIRE: Clip = {
  id: 'soldier-fire',
  role: 'primary-attack',
  loop: false,
  rootMotion: 'in-place',
  durationSeconds: SOLDIER_FIRE_DURATION,
  contactEvents: [{ u: 0, name: 'muzzle' }],
  tracks: [
    recoil('upperArmR', -0.12), // right shoulder kicks up/back
    recoil('upperArmL', -0.05),
    recoil('foreArmR', -0.06),
    recoil('spine', -0.06), // brace against the stock
    recoil('head', 0.02),
  ],
  provenance: 'authored keyframes (plan 07); envelope reuses the P5.3 recoil saw-tooth (recoilAmp)',
};

export const SOLDIER_CLIP_DEATH: Clip = {
  id: 'soldier-death',
  role: 'death',
  loop: false,
  rootMotion: 'in-place',
  durationSeconds: SOLDIER_DEATH_DURATION,
  contactEvents: [{ u: 0.55, name: 'body-contact' }],
  tracks: [
    {
      // Fall backward about the feet pivot (model faces +z; the threats are ahead).
      bone: 'root',
      keys: [
        { u: 0, rot: [0, 0, 0], pos: [0, 0, 0] },
        { u: 0.1, rot: [-0.4, 0.1, 0], pos: [0, 0, 0] },
        { u: 0.3, rot: [-1.1, 0.2, 0.05], pos: [0, 0.1, 0] },
        { u: 0.55, rot: [-1.5, 0.25, 0.05], pos: [0, 0.2, 0] }, // prone on the back, pack lifted clear of the deck
        { u: 1, rot: [-1.5, 0.25, 0.05], pos: [0, 0.2, 0] },
      ],
    },
    {
      // The rifle lies along the body instead of standing straight up from the chest (the net
      // fall pitch is ≈ −1.45, so this cancels it and leaves the muzzle pointing at the boots).
      bone: 'weapon',
      keys: [
        { u: 0, rot: [0, 0, 0], pos: [0, 0, 0] },
        { u: 0.55, rot: [0.8, 0, 0], pos: [0, 0, -0.2] }, // settle onto the chest (spine +z is "up" once prone)
        { u: 1, rot: [0.8, 0, 0], pos: [0, 0, -0.2] },
      ],
    },
    {
      bone: 'hips',
      keys: [
        { u: 0, rot: [0, 0, 0] },
        { u: 0.3, rot: [0.3, 0, 0] },
        { u: 0.55, rot: [0.35, 0, 0] },
        { u: 1, rot: [0.35, 0, 0] },
      ],
    },
    {
      bone: 'spine',
      keys: [
        { u: 0, rot: [0, 0, 0] },
        { u: 0.3, rot: [-0.25, 0, 0] },
        { u: 0.55, rot: [-0.3, 0, 0] },
        { u: 1, rot: [-0.3, 0, 0] },
      ],
    },
    {
      bone: 'head',
      keys: [
        { u: 0, rot: [0.1, 0, 0] },
        { u: 0.3, rot: [0.3, 0, 0] },
        { u: 0.55, rot: [0.35, 0, 0] },
        { u: 1, rot: [0.35, 0, 0] },
      ],
    },
    {
      bone: 'upperArmL',
      keys: [
        { u: 0, rot: [0, 0, 0] },
        { u: 0.3, rot: [0, 0, 0.8] },
        { u: 0.55, rot: [0, 0, 0.9] },
        { u: 1, rot: [0, 0, 0.9] },
      ],
    },
    {
      bone: 'upperArmR',
      keys: [
        { u: 0, rot: [0, 0, 0] },
        { u: 0.3, rot: [0, 0, -0.8] },
        { u: 0.55, rot: [0, 0, -0.9] },
        { u: 1, rot: [0, 0, -0.9] },
      ],
    },
    {
      bone: 'foreArmL',
      keys: [
        { u: 0, rot: [0, 0, 0] },
        { u: 0.55, rot: [0.6, 0, 0] },
        { u: 1, rot: [0.6, 0, 0] },
      ],
    },
    {
      bone: 'foreArmR',
      keys: [
        { u: 0, rot: [0, 0, 0] },
        { u: 0.55, rot: [0.6, 0, 0] },
        { u: 1, rot: [0.6, 0, 0] },
      ],
    },
    {
      bone: 'thighL',
      keys: [
        { u: 0, rot: [0, 0, 0] },
        { u: 0.55, rot: [-0.55, 0, 0] },
        { u: 1, rot: [-0.55, 0, 0] },
      ],
    },
    {
      bone: 'thighR',
      keys: [
        { u: 0, rot: [0, 0, 0] },
        { u: 0.55, rot: [-0.4, 0, 0] },
        { u: 1, rot: [-0.4, 0, 0] },
      ],
    },
    {
      bone: 'shinL',
      keys: [
        { u: 0, rot: [0, 0, 0] },
        { u: 0.55, rot: [0.65, 0, 0] },
        { u: 1, rot: [0.65, 0, 0] },
      ],
    },
    {
      bone: 'shinR',
      keys: [
        { u: 0, rot: [0, 0, 0] },
        { u: 0.55, rot: [0.5, 0, 0] },
        { u: 1, rot: [0.5, 0, 0] },
      ],
    },
    {
      bone: 'footL',
      keys: [
        { u: 0, rot: [0.2, 0, 0] },
        { u: 0.55, rot: [0.45, 0, 0] },
        { u: 1, rot: [0.45, 0, 0] },
      ],
    },
    {
      bone: 'footR',
      keys: [
        { u: 0, rot: [0.2, 0, 0] },
        { u: 0.55, rot: [0.4, 0, 0] },
        { u: 1, rot: [0.4, 0, 0] },
      ],
    },
  ],
  provenance: 'authored keyframes (plan 07); driven by the sim losses log (pure age ⇒ pose)',
};

// ---------------------------------------------------------------------------
// Boss clips (monster profile; prototype lanes: idle / primary-attack / hit / death)
// ---------------------------------------------------------------------------

export const BOSS_IDLE_DURATION = 4.2;
export const BOSS_HIT_DURATION = 0.3;
export const BOSS_DEATH_DURATION = 1.6;
/** Post-impact recover window the slam clip's second half plays over. */
export const BOSS_SLAM_RECOVER = 0.5;
const BOSS_DEATH_XFADE = 0.15;
/** Flinch weight decay τ (s) from (sim.t − bossHitAt). */
export const BOSS_HIT_TAU = 0.22;

function sway(bone: string, axis: 0 | 1 | 2, peak: number, invert = false): BoneTrack {
  const s = invert ? -1 : 1;
  const key = (u: number, v: number): ClipKey => {
    const rot: [number, number, number] = [0, 0, 0];
    rot[axis] = s * v;
    return { u, rot };
  };
  return {
    bone,
    keys: [key(0, peak), key(0.25, 0), key(0.5, -peak), key(0.75, 0), key(1, peak)],
  };
}

export const BOSS_CLIP_IDLE: Clip = {
  id: 'boss-idle',
  role: 'idle',
  loop: true,
  rootMotion: 'in-place',
  durationSeconds: BOSS_IDLE_DURATION,
  contactEvents: [],
  tracks: [
    sway('torso', 0, 0.03), // breathing pitch
    sway('torso', 1, 0.04), // weight shift
    sway('head', 1, 0.05, true), // head counter-yaw
    sway('head', 0, 0.03),
    sway('hips', 1, 0.03, true),
    sway('upperArmL', 0, 0.03),
    sway('upperArmR', 0, 0.02, true),
    {
      bone: 'root',
      keys: [
        { u: 0, rot: [0, 0, 0], pos: [0, 0, 0] },
        { u: 0.25, rot: [0, 0, 0], pos: [0, 0.02, 0] },
        { u: 0.5, rot: [0, 0, 0], pos: [0, 0.04, 0] },
        { u: 0.75, rot: [0, 0, 0], pos: [0, 0.02, 0] },
        { u: 1, rot: [0, 0, 0], pos: [0, 0, 0] },
      ],
    },
  ],
  provenance: 'authored keyframes (plan 07); replaces the merged-mesh breathing approximation',
};

/** Slam phase mapping: windup is the first half (telegraph progress), strike+recover the second. */
export const BOSS_SLAM_WINDUP_END = 0.5;

export const BOSS_CLIP_SLAM: Clip = {
  id: 'boss-slam',
  role: 'primary-attack',
  loop: false,
  rootMotion: 'in-place',
  durationSeconds: 1.3 + BOSS_SLAM_RECOVER, // nominal; u is mapped from the sim slam timers
  contactEvents: [
    { u: BOSS_SLAM_WINDUP_END, name: 'windup-full' },
    { u: 0.55, name: 'impact' },
  ],
  tracks: [
    {
      bone: 'torso',
      keys: [
        { u: 0, rot: [0, 0, 0] },
        { u: 0.3, rot: [-0.14, -0.2, 0] },
        { u: 0.5, rot: [-0.22, -0.35, 0] },
        { u: 0.55, rot: [0.12, 0.1, 0] },
        { u: 0.7, rot: [0.3, 0.25, 0] },
        { u: 1, rot: [0, 0, 0] },
      ],
    },
    {
      bone: 'hips',
      keys: [
        { u: 0, rot: [0, 0, 0] },
        { u: 0.5, rot: [0, 0.12, 0] },
        { u: 0.7, rot: [0, -0.12, 0] },
        { u: 1, rot: [0, 0, 0] },
      ],
    },
    {
      bone: 'upperArmR',
      keys: [
        { u: 0, rot: [0, 0, 0] },
        { u: 0.3, rot: [-0.5, 0, 0] },
        { u: 0.5, rot: [-0.85, 0, 0] },
        { u: 0.55, rot: [-0.1, 0, 0] },
        { u: 0.7, rot: [0.55, 0, 0] },
        { u: 1, rot: [0, 0, 0] },
      ],
    },
    {
      bone: 'foreArmR',
      keys: [
        { u: 0, rot: [0, 0, 0] },
        { u: 0.5, rot: [-0.35, 0, 0] },
        { u: 0.7, rot: [0.2, 0, 0] },
        { u: 1, rot: [0, 0, 0] },
      ],
    },
    {
      bone: 'head',
      keys: [
        { u: 0, rot: [0, 0, 0] },
        { u: 0.5, rot: [-0.15, 0, 0] },
        { u: 0.7, rot: [0.15, 0, 0] },
        { u: 1, rot: [0, 0, 0] },
      ],
    },
    {
      bone: 'thighL',
      keys: [
        { u: 0, rot: [0, 0, 0] },
        { u: 0.5, rot: [-0.08, 0, 0] },
        { u: 0.7, rot: [0.18, 0, 0] },
        { u: 1, rot: [0, 0, 0] },
      ],
    },
    {
      bone: 'thighR',
      keys: [
        { u: 0, rot: [0, 0, 0] },
        { u: 0.5, rot: [-0.08, 0, 0] },
        { u: 0.7, rot: [0.18, 0, 0] },
        { u: 1, rot: [0, 0, 0] },
      ],
    },
    {
      bone: 'shinL',
      keys: [
        { u: 0, rot: [0, 0, 0] },
        { u: 0.5, rot: [0.08, 0, 0] },
        { u: 0.7, rot: [-0.2, 0, 0] },
        { u: 1, rot: [0, 0, 0] },
      ],
    },
    {
      bone: 'shinR',
      keys: [
        { u: 0, rot: [0, 0, 0] },
        { u: 0.5, rot: [0.08, 0, 0] },
        { u: 0.7, rot: [-0.2, 0, 0] },
        { u: 1, rot: [0, 0, 0] },
      ],
    },
  ],
  provenance: 'authored keyframes (plan 07); u mapped from slam.t (telegraph progress / post-impact)',
};

export const BOSS_CLIP_HIT: Clip = {
  id: 'boss-hit',
  role: 'hit',
  loop: false,
  rootMotion: 'in-place',
  durationSeconds: BOSS_HIT_DURATION,
  contactEvents: [],
  tracks: [
    {
      bone: 'torso',
      keys: [
        { u: 0, rot: [-0.14, -0.08, 0] },
        { u: 0.4, rot: [-0.06, -0.03, 0] },
        { u: 1, rot: [0, 0, 0] },
      ],
    },
    {
      bone: 'head',
      keys: [
        { u: 0, rot: [-0.12, 0, 0] },
        { u: 0.4, rot: [-0.05, 0, 0] },
        { u: 1, rot: [0, 0, 0] },
      ],
    },
    {
      bone: 'upperArmL',
      keys: [
        { u: 0, rot: [0.08, 0, 0] },
        { u: 1, rot: [0, 0, 0] },
      ],
    },
    {
      bone: 'upperArmR',
      keys: [
        { u: 0, rot: [0.08, 0, 0] },
        { u: 1, rot: [0, 0, 0] },
      ],
    },
  ],
  provenance: 'authored keyframes (plan 07); weight = exp(−(t − bossHitAt)/τ) from sim telemetry',
};

export const BOSS_CLIP_DEATH: Clip = {
  id: 'boss-death',
  role: 'death',
  loop: false,
  rootMotion: 'in-place',
  durationSeconds: BOSS_DEATH_DURATION,
  contactEvents: [{ u: 0.8, name: 'body-contact' }],
  tracks: [
    {
      // Fall toward the squad (model faces −z): tips forward about the feet pivot, then settles.
      bone: 'root',
      keys: [
        { u: 0, rot: [0, 0, 0], pos: [0, 0, 0] },
        { u: 0.15, rot: [-0.35, 0.05, 0], pos: [0, 0, 0] },
        { u: 0.45, rot: [-0.95, 0.1, 0.05], pos: [0, 0.15, 0] },
        { u: 0.8, rot: [-1.35, 0.12, 0.05], pos: [0, 0.35, 0] },
        { u: 1, rot: [-1.35, 0.12, 0.05], pos: [0, 0.35, 0] },
      ],
    },
    {
      bone: 'torso',
      keys: [
        { u: 0, rot: [0, 0, 0] },
        { u: 0.45, rot: [0.2, 0, 0] },
        { u: 0.8, rot: [0.35, 0, 0] },
        { u: 1, rot: [0.35, 0, 0] },
      ],
    },
    {
      bone: 'head',
      keys: [
        { u: 0, rot: [0, 0, 0] },
        { u: 0.45, rot: [0.2, 0, 0] },
        { u: 0.8, rot: [0.3, 0, 0] },
        { u: 1, rot: [0.3, 0, 0] },
      ],
    },
    {
      bone: 'upperArmR',
      keys: [
        { u: 0, rot: [0, 0, 0] },
        { u: 0.45, rot: [0.7, 0, 0] },
        { u: 0.8, rot: [1.0, 0, 0] },
        { u: 1, rot: [1.0, 0, 0] },
      ],
    },
    {
      bone: 'upperArmL',
      keys: [
        { u: 0, rot: [0, 0, 0] },
        { u: 0.45, rot: [0.55, 0, 0] },
        { u: 0.8, rot: [0.8, 0, 0] },
        { u: 1, rot: [0.8, 0, 0] },
      ],
    },
    {
      bone: 'foreArmL',
      keys: [
        { u: 0, rot: [0, 0, 0] },
        { u: 0.8, rot: [0.6, 0, 0] },
        { u: 1, rot: [0.6, 0, 0] },
      ],
    },
    {
      bone: 'foreArmR',
      keys: [
        { u: 0, rot: [0, 0, 0] },
        { u: 0.8, rot: [0.6, 0, 0] },
        { u: 1, rot: [0.6, 0, 0] },
      ],
    },
    {
      bone: 'thighL',
      keys: [
        { u: 0, rot: [0, 0, 0] },
        { u: 0.45, rot: [0.4, 0, 0] },
        { u: 0.8, rot: [0.5, 0, 0] },
        { u: 1, rot: [0.5, 0, 0] },
      ],
    },
    {
      bone: 'thighR',
      keys: [
        { u: 0, rot: [0, 0, 0] },
        { u: 0.45, rot: [0.4, 0, 0] },
        { u: 0.8, rot: [0.5, 0, 0] },
        { u: 1, rot: [0.5, 0, 0] },
      ],
    },
    {
      bone: 'shinL',
      keys: [
        { u: 0, rot: [0, 0, 0] },
        { u: 0.45, rot: [-0.5, 0, 0] },
        { u: 0.8, rot: [-0.6, 0, 0] },
        { u: 1, rot: [-0.6, 0, 0] },
      ],
    },
    {
      bone: 'shinR',
      keys: [
        { u: 0, rot: [0, 0, 0] },
        { u: 0.45, rot: [-0.5, 0, 0] },
        { u: 0.8, rot: [-0.6, 0, 0] },
        { u: 1, rot: [-0.6, 0, 0] },
      ],
    },
    {
      bone: 'footL',
      keys: [
        { u: 0, rot: [0, 0, 0] },
        { u: 0.8, rot: [-0.55, 0, 0] },
        { u: 1, rot: [-0.55, 0, 0] },
      ],
    },
    {
      bone: 'footR',
      keys: [
        { u: 0, rot: [0, 0, 0] },
        { u: 0.8, rot: [-0.55, 0, 0] },
        { u: 1, rot: [-0.55, 0, 0] },
      ],
    },
  ],
  provenance: 'authored keyframes (plan 07); driven by sim.wonAt (pure age ⇒ pose), holds as corpse',
};

// ---------------------------------------------------------------------------
// Compiled form + sampler (runtime: index math only, zero string lookups)
// ---------------------------------------------------------------------------

import type { Rig } from './rigs';

export interface Pose {
  /** Per-bone bind-relative euler [rx, ry, rz], bone-major (3 floats per bone, rig order). */
  rot: Float32Array;
  /** Per-bone bind-relative position offset [dx, dy, dz], rig order. */
  pos: Float32Array;
  boneCount: number;
}

export function createPose(rig: Rig): Pose {
  return { rot: new Float32Array(rig.bones.length * 3), pos: new Float32Array(rig.bones.length * 3), boneCount: rig.bones.length };
}

export function clearPose(pose: Pose): void {
  pose.rot.fill(0);
  pose.pos.fill(0);
}

interface CompiledTrack {
  bone: number;
  us: Float32Array;
  rots: Float32Array;
  hasPos: boolean;
  poss: Float32Array;
}

export interface CompiledClip {
  id: string;
  role: ClipRole;
  loop: boolean;
  durationSeconds: number;
  contactEvents: readonly ContactEvent[];
  tracks: readonly CompiledTrack[];
}

/** Compile authoring form → runtime form (bone indices, typed arrays). Throws on unknown bones. */
export function compileClip(rig: Rig, clip: Clip): CompiledClip {
  const index = new Map(rig.bones.map((b, i) => [b.name, i] as const));
  const tracks: CompiledTrack[] = [];
  for (const track of clip.tracks) {
    const bone = index.get(track.bone);
    if (bone === undefined) throw new Error(`[clips] clip ${clip.id}: bone "${track.bone}" not in rig ${rig.id}`);
    const us = new Float32Array(track.keys.length);
    const rots = new Float32Array(track.keys.length * 3);
    const hasPos = track.keys.some((k) => k.pos !== undefined);
    const poss = new Float32Array(track.keys.length * 3);
    track.keys.forEach((key, i) => {
      us[i] = key.u;
      rots.set(key.rot, i * 3);
      if (key.pos) poss.set(key.pos, i * 3);
    });
    tracks.push({ bone, us, rots, hasPos, poss });
  }
  return { id: clip.id, role: clip.role, loop: clip.loop, durationSeconds: clip.durationSeconds, contactEvents: clip.contactEvents, tracks };
}

/**
 * Sample a compiled clip at normalized time u into `out` (ADDITIVE on out — callers clear first or
 * layer deliberately). Loop clips wrap u; one-shot clips clamp to [0, 1]. Linear interpolation.
 */
export function sampleClip(clip: CompiledClip, u: number, out: Pose): void {
  if (clip.loop) {
    u = u - Math.floor(u);
  } else {
    u = u < 0 ? 0 : u > 1 ? 1 : u;
  }
  for (const track of clip.tracks) {
    const { us, rots } = track;
    const n = us.length;
    // Find the bracketing key pair (keys are authored sorted; last key wins past the end).
    let i = 0;
    while (i < n - 1 && us[i + 1]! <= u) i++;
    const u0 = us[i]!;
    const u1 = i + 1 < n ? us[i + 1]! : u0 + 1;
    const span = u1 - u0;
    const t = span > 0 ? (u - u0) / span : 0;
    const o = track.bone * 3;
    for (let c = 0; c < 3; c++) {
      const a = rots[i * 3 + c]!;
      const b = rots[(i + 1 < n ? i + 1 : i) * 3 + c]!;
      out.rot[o + c] = out.rot[o + c]! + (a + (b - a) * t);
    }
    if (track.hasPos) {
      for (let c = 0; c < 3; c++) {
        const a = track.poss[i * 3 + c]!;
        const b = track.poss[(i + 1 < n ? i + 1 : i) * 3 + c]!;
        out.pos[o + c] = out.pos[o + c]! + (a + (b - a) * t);
      }
    }
  }
}

/** out += delta × w (additive clip layer). */
export function addScaledInto(out: Pose, delta: Pose, w: number): void {
  for (let i = 0; i < out.rot.length; i++) out.rot[i] = out.rot[i]! + delta.rot[i]! * w;
  for (let i = 0; i < out.pos.length; i++) out.pos[i] = out.pos[i]! + delta.pos[i]! * w;
}

/** out ← out × (1−a) + target × a (crossfade toward an absolute clip). */
export function lerpInto(out: Pose, target: Pose, a: number): void {
  for (let i = 0; i < out.rot.length; i++) out.rot[i] = out.rot[i]! * (1 - a) + target.rot[i]! * a;
  for (let i = 0; i < out.pos.length; i++) out.pos[i] = out.pos[i]! * (1 - a) + target.pos[i]! * a;
}

// ---------------------------------------------------------------------------
// Per-actor pose evaluation (pure SimState ⇒ Pose)
// ---------------------------------------------------------------------------

import { effectiveFireInterval, type SimState } from '../../sim/sim';

/** The static level data the pose evaluation needs (structurally satisfied by LevelSpec / MotionLevel). */
export interface PoseLevel {
  weapons: { fireInterval: number }[];
}

export interface SoldierPoseInputs {
  sim: SimState;
  level: PoseLevel;
  /** Slot index (formation identity). */
  slot: number;
  /** Formation hash for this slot (stableHash(row, col)) — phase identity. */
  hash: number;
  /** Plan 10: this slot's stride rate (actors.ts gaitHz); defaults to the pinned 1.9 Hz. */
  hz?: number;
}

/** Walk-cycle phase for a slot: the bob family's argument (TAU·hz·t + hash·TAU). */
export function soldierGaitU(t: number, hash: number, hz = 1.9): number {
  const phase = 2 * Math.PI * hz * t + hash * 2 * Math.PI;
  return (phase - Math.floor(phase / (2 * Math.PI)) * 2 * Math.PI) / (2 * Math.PI);
}

/** Fire-envelope inputs, mirroring recoilAmp (actors.ts) exactly: the cooldown gate separates
 *  rest (cooldown 0 — no flinch) from a fresh volley (cooldown just under the interval). */
export function soldierFire(fireCooldown: number, interval: number, hash: number): { weight: number; u: number } {
  if (!(fireCooldown > 0) || !(fireCooldown <= interval)) return { weight: 0, u: 0 };
  const tau = interval - fireCooldown; // age of the most recent volley
  const onset = (hash - Math.floor(hash)) * Math.min(0.07, interval);
  const age = tau - onset;
  if (age < 0) return { weight: 0, u: 0 };
  return { weight: Math.exp(-age / 0.07), u: Math.min(1, age / SOLDIER_FIRE_DURATION) };
}

/** Look up the death entry for a slot, if one is live (within the hold and still beyond the
 *  living formation — a regrown formation "replaces" the fallen soldier). Pure. */
export interface DeathState {
  /** Clip-normalized age. */
  u: number;
  /** Crossfade weight toward the death clip. */
  blend: number;
  /** True once the clip is fully blended (corpse). */
  settled: boolean;
}

export function soldierDeath(sim: SimState, slot: number): DeathState | null {
  const entry = sim.losses.find((l) => l.slot === slot);
  if (!entry) return null;
  if (slot < sim.n) return null; // formation regrew past this corpse: replaced, not fallen
  const age = sim.t - entry.t;
  if (age < 0 || age > SOLDIER_CORPSE_HOLD) return null;
  const u = Math.min(1, age / SOLDIER_DEATH_DURATION);
  const blend = Math.min(1, u / DEATH_XFADE);
  return { u, blend, settled: u >= 1 };
}

/** Full per-soldier pose: walk base + additive fire recoil, crossfaded to death when fallen. */
export function soldierPoseInto(inputs: SoldierPoseInputs, clips: { walk: CompiledClip; fire: CompiledClip; death: CompiledClip }, out: Pose, scratch: Pose, scratch2: Pose): void {
  const { sim, level, hash } = inputs;
  clearPose(out);
  // Base: walk (bind-relative, looped at the gait phase).
  sampleClip(clips.walk, soldierGaitU(sim.t, hash, inputs.hz), out);
  // Additive: fire recoil keyed off the same saw-tooth the procedural crowd uses.
  const fire = soldierFire(sim.fireCooldown, effectiveFireInterval(level.weapons, sim), hash);
  if (fire.weight > 0) {
    clearPose(scratch);
    sampleClip(clips.fire, fire.u, scratch);
    addScaledInto(out, scratch, fire.weight);
  }
  // Death: absolute clip replaces the blend via a short crossfade, then holds.
  const death = soldierDeath(sim, inputs.slot);
  if (death) {
    clearPose(scratch2);
    sampleClip(clips.death, death.u, scratch2);
    lerpInto(out, scratch2, death.blend);
  }
}

/** Slam clip u from the sim slam timers (the slam envelope boundaries — clips.test.ts pins the
 *  windup/strike continuity): telegraph progress maps to [0, WINDUP_END]; post-impact slam.t maps to
 *  (WINDUP_END, 1]. */
export function bossSlamU(sim: SimState, spec: { slamPeriod: number; slamTelegraph: number }): number | null {
  if (sim.phase !== 'boss' || !sim.slam) return null;
  if (sim.slam.active === 'telegraph') {
    const progress = (sim.slam.t - (spec.slamPeriod - spec.slamTelegraph)) / spec.slamTelegraph;
    const p = progress < 0 ? 0 : progress > 1 ? 1 : progress;
    return p * BOSS_SLAM_WINDUP_END;
  }
  // 'silent' just after the strike: recover window; older silence ⇒ no slam pose.
  const u = BOSS_SLAM_WINDUP_END + ((1 - BOSS_SLAM_WINDUP_END) * Math.min(1, sim.slam.t / BOSS_SLAM_RECOVER));
  return sim.slam.t <= BOSS_SLAM_RECOVER ? u : null;
}

/** Boss flinch weight from telemetry: exp decay from the last hp decrease. */
export function bossHitWeight(sim: SimState): number {
  if (sim.bossHitAt < 0) return 0;
  const age = sim.t - sim.bossHitAt;
  if (age < 0) return 0;
  return Math.exp(-age / BOSS_HIT_TAU);
}

/** Boss death age (clip-normalized) from wonAt, null before the win. */
export function bossDeathU(sim: SimState, presentationAge?: number): number | null {
  const age = bossDeathAge(sim, presentationAge);
  return age === null ? null : Math.min(1, age / BOSS_DEATH_DURATION);
}

/** Full boss pose: idle base + additive slam + additive flinch, crossfaded to death. */
export function bossPoseInto(
  sim: SimState,
  spec: { slamPeriod: number; slamTelegraph: number },
  clips: { idle: CompiledClip; slam: CompiledClip; hit: CompiledClip; death: CompiledClip },
  out: Pose,
  scratch: Pose,
  scratch2: Pose,
  presentationAge?: number,
): void {
  clearPose(out);
  sampleClip(clips.idle, sim.t / BOSS_IDLE_DURATION, out);
  const slamU = bossSlamU(sim, spec);
  if (slamU !== null) {
    clearPose(scratch);
    sampleClip(clips.slam, slamU, scratch);
    addScaledInto(out, scratch, 1);
  }
  const hitW = bossHitWeight(sim);
  if (hitW > 0.01) {
    clearPose(scratch);
    // audit #10: sample FORWARD — the hit clip is authored max-flinch at u=0 settling to bind at u=1,
    // so the old 1−age read the bind pose at the impact frame (no flinch at weight 1) and peaked the
    // deflection only at 0.3 s, by which time the weight is down to ~¼. The sibling additive
    // (soldierFire) always sampled forward.
    sampleClip(clips.hit, Math.min(1, (sim.t - sim.bossHitAt) / BOSS_HIT_DURATION), scratch);
    addScaledInto(out, scratch, hitW);
  }
  const deathU = bossDeathU(sim, presentationAge);
  if (deathU !== null) {
    clearPose(scratch2);
    sampleClip(clips.death, deathU, scratch2);
    const blend = Math.min(1, deathU / BOSS_DEATH_XFADE);
    lerpInto(out, scratch2, blend);
  }
}
